import {
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { REVIEW_INCIDENCE_TYPES, REVIEW_STATES, TReviewStates } from '@/constants';
import { ReviewStateEnum, TReviewStateValues } from '@/features/segments/types';
import { EReviewIncidenceType } from '@/types';

type TIncidenceType = {
  review_incidence_type?: EReviewIncidenceType;
  review_incidence_note?: string | null;
  reviewed_by_user_id?: string | null;
};

type TStatusSeenChipInTableProps<T = TIncidenceType, R = ReviewStateEnum> = {
  review_state: TReviewStateValues | string;
  classname?: string;
  iconSize?: IconSizeEnum;
  fontSize?: FontSizeEnum;
  fontWeight?: FontWeightEnum;
  ignoreTooltip?: boolean;
  item?: T;
  reviewStates?: TReviewStates<R>[];
};

export function StatusSeenChipInTable<T = TIncidenceType, R = ReviewStateEnum>({
  review_state,
  classname,
  iconSize,
  fontSize,
  fontWeight,
  item,
  ignoreTooltip,
  reviewStates = REVIEW_STATES as unknown as TReviewStates<R>[],
}: TStatusSeenChipInTableProps<T, R>) {
  const selectedReviewState = useMemo(
    () => reviewStates.find(review => review.value === Number(review_state)),
    [review_state, reviewStates]
  );
  const statusClass = selectedReviewState?.class || 'bg-neutral-30';
  const Icon = selectedReviewState?.icon;
  const { t } = useTranslation();

  const incidenceTooltipMessage = useMemo(() => {
    const incidenceItem = item as TIncidenceType;
    const reviewIncidenceType = incidenceItem?.review_incidence_type || '';
    const reviewIncidenceNote = incidenceItem?.review_incidence_note || '';

    const incidenceMessageType =
      REVIEW_INCIDENCE_TYPES.find(incidence => incidence.value === reviewIncidenceType)?.label ||
      '';

    return [incidenceMessageType, reviewIncidenceNote].filter(Boolean).join(' - ');
  }, [item]);

  return (
    <>
      {review_state === 5 && item && !ignoreTooltip ? (
        <Tooltip help={incidenceTooltipMessage} grow>
          <Typography
            fontSize={fontSize || FontSizeEnum.Xs}
            weight={fontWeight || FontWeightEnum.Semibold}
            className={clsx(statusClass, classname)}
          >
            {Icon && <Icon size={iconSize || IconSizeEnum.XS} />}
            {t(selectedReviewState?.label || '')}
          </Typography>
        </Tooltip>
      ) : (
        <Typography
          fontSize={fontSize || FontSizeEnum.Xs}
          weight={fontWeight || FontWeightEnum.Semibold}
          className={clsx(statusClass, classname)}
        >
          {Icon && <Icon size={iconSize || IconSizeEnum.XS} />}
          {t(selectedReviewState?.label || '')}
        </Typography>
      )}
    </>
  );
}
